import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Anchor, Users, Gauge, Ship } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import YachtGallery from "@/components/YachtGallery";
import SEO from "@/components/SEO";
import { yachts } from "@/data/yachts";

const ease = [0.22, 1, 0.36, 1] as const;

const YachtDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const yacht = yachts.find((y) => y.slug === slug);

  if (!yacht) return <Navigate to="/yachts" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title={`${yacht.name} — Arzani Yachts`}
        description={(yacht.description || `${yacht.name} private yacht charter through Arzani Group.`).slice(0, 155)}
        path={`/yachts/${yacht.slug}`}
        image={yacht.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: yacht.name,
          description: yacht.description,
          image: yacht.image,
          brand: { "@type": "Brand", name: "Arzani Yachts" },
        }}
      />
      <Navigation />

      {/* Hero Image */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <YachtGallery
            images={yacht.images || [yacht.image]}
            name={`${yacht.name} "${yacht.boatName}"`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-foreground/30 pointer-events-none" />
        </div>
        <div className="relative z-10 container-luxury pb-16 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 1.2, ease }}
          >
            <Link
              to="/yachts"
              className="inline-flex items-center gap-2 font-label text-primary-foreground/50 hover:text-primary-foreground transition-colors duration-500 mb-6"
            >
              <ArrowLeft size={14} />
              Back to fleet
            </Link>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground tracking-wide uppercase mb-2">
              {yacht.name}
            </h1>
            <p className="font-accent text-primary-foreground/60 text-lg md:text-xl italic">
              "{yacht.boatName}"
            </p>
          </motion.div>
        </div>
      </section>

      {/* Details */}
      <section className="section-spacing bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-28">
            {/* Left: Description */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease }}
            >
              <span className="font-label text-bronze block mb-6">About This Yacht</span>
              <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9] mb-10">
                {yacht.description}
              </p>

              {/* Specs Grid */}
              <div className="grid grid-cols-2 gap-6 mb-10">
                <div className="flex items-start gap-3">
                  <Ship size={18} className="text-bronze mt-0.5 shrink-0" />
                  <div>
                    <span className="font-label text-foreground/40 block text-[10px] mb-1">Length</span>
                    <span className="font-body text-foreground text-sm">{yacht.length}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users size={18} className="text-bronze mt-0.5 shrink-0" />
                  <div>
                    <span className="font-label text-foreground/40 block text-[10px] mb-1">Guests</span>
                    <span className="font-body text-foreground text-sm">{yacht.guests}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Gauge size={18} className="text-bronze mt-0.5 shrink-0" />
                  <div>
                    <span className="font-label text-foreground/40 block text-[10px] mb-1">Cruising Speed</span>
                    <span className="font-body text-foreground text-sm">{yacht.cruisingSpeed}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Anchor size={18} className="text-bronze mt-0.5 shrink-0" />
                  <div>
                    <span className="font-label text-foreground/40 block text-[10px] mb-1">Marina</span>
                    <span className="font-body text-foreground text-sm">{yacht.marina}</span>
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <span className="font-label text-foreground/40 block text-[10px] mb-2">Cabins</span>
                <span className="font-body text-foreground text-sm">{yacht.cabins}</span>
              </div>
              <div className="mb-8">
                <span className="font-label text-foreground/40 block text-[10px] mb-2">Crew</span>
                <span className="font-body text-foreground text-sm">{yacht.crew}</span>
              </div>
              <div>
                <span className="font-label text-foreground/40 block text-[10px] mb-2">Features</span>
                <span className="font-body text-foreground text-sm">{yacht.features}</span>
              </div>
            </motion.div>

            {/* Right: Pricing + Includes */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.15, ease }}
            >
              <span className="font-label text-bronze block mb-6">Pricing · Season 2026</span>

              {/* Pricing Table */}
              <div className="border border-border/30 mb-10">
                <div className="grid grid-cols-3 border-b border-border/30">
                  <div className="p-5 border-r border-border/30 text-center">
                    <span className="font-label text-foreground/40 block text-[10px] mb-2">Low Season</span>
                    <span className="font-body text-muted-foreground text-xs block mb-1">Rest of the year</span>
                    <span className="font-display-medium text-foreground text-xl md:text-2xl">{yacht.pricing.low}</span>
                  </div>
                  <div className="p-5 border-r border-border/30 text-center">
                    <span className="font-label text-foreground/40 block text-[10px] mb-2">Mid Season</span>
                    <span className="font-body text-muted-foreground text-xs block mb-1">June / September</span>
                    <span className="font-display-medium text-foreground text-xl md:text-2xl">{yacht.pricing.mid}</span>
                  </div>
                  <div className="p-5 text-center">
                    <span className="font-label text-foreground/40 block text-[10px] mb-2">High Season</span>
                    <span className="font-body text-muted-foreground text-xs block mb-1">July / August</span>
                    <span className="font-display-medium text-foreground text-xl md:text-2xl">{yacht.pricing.high}</span>
                  </div>
                </div>
                <div className="p-4">
                  <p className="font-body text-muted-foreground/60 text-xs italic text-center">
                    *{yacht.priceNote}
                  </p>
                </div>
              </div>

              {/* Price Includes */}
              <div className="mb-12">
                <span className="font-label text-foreground/40 block text-[10px] mb-4">Price Includes</span>
                <div className="flex flex-wrap gap-3">
                  {yacht.includes.map((item) => (
                    <span
                      key={item}
                      className="font-body text-foreground text-xs border border-border/30 px-4 py-2"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="border-t border-border/20 pt-10">
                <h3 className="font-display-medium text-lg text-foreground tracking-wide uppercase mb-4">
                  Interested in this yacht?
                </h3>
                <p className="font-body text-muted-foreground text-sm leading-[1.8] mb-6">
                  Send us your preferred date, destination and any special requests. 
                  We'll confirm availability and arrange every detail.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={`https://wa.me/message/2XEKJ5STR2IZJ1`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-8 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500 text-center"
                  >
                    WhatsApp
                  </a>
                  <a
                    href={`mailto:info@arzanigroupworldwide.com?subject=Yacht Inquiry: ${yacht.name} "${yacht.boatName}"`}
                    className="inline-block px-8 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500 text-center"
                  >
                    Email Us
                  </a>
                </div>
                <div className="mt-8 border-t border-border/10 pt-6">
                  <p className="font-body text-muted-foreground/50 text-xs leading-[1.9] italic">
                    Every yacht can be reserved for 24 hours, allowing you time to confirm your plans with ease.
                    To secure the booking, a first deposit is required within this period. Once the reservation is confirmed, the remaining balance can be completed on the day of departure.
                  </p>
                  <p className="font-body text-muted-foreground/50 text-xs leading-[1.9] italic mt-3">
                    A simple and flexible process — designed so your time is spent looking forward to the sea.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Booking Process */}
      <section className="section-spacing bg-warm-black">
        <div className="container-luxury max-w-4xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">How It Works</span>
            <h2 className="font-display text-3xl md:text-4xl text-primary-foreground tracking-wide uppercase">
              Yacht Requests
            </h2>
            <p className="font-body text-primary-foreground/40 text-sm mt-4">
              A simple process, designed with ease in mind.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {[
              {
                step: "1",
                title: "Choose your yacht",
                text: "Browse our collection or simply send us a message. Whether you already have a yacht in mind or prefer guidance, our team will assist you in selecting the right vessel for your day at sea.",
              },
              {
                step: "2",
                title: "Share your preferences",
                text: "Let us know your desired date, destination and budget. We quietly check availability and arrange every detail with care.",
              },
              {
                step: "3",
                title: "Enjoy the experience",
                text: "Once confirmed, everything is prepared for your arrival. Step aboard, unwind, and allow the sea to set the rhythm of your day.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: i * 0.15, ease }}
              >
                <span className="font-display text-5xl text-primary-foreground/10 block mb-4">
                  {item.step}
                </span>
                <h3 className="font-display-medium text-base text-primary-foreground tracking-wide uppercase mb-4">
                  {item.title}
                </h3>
                <p className="font-body text-primary-foreground/50 text-sm leading-[1.9]">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 1, ease }}
          >
            <p className="font-label text-primary-foreground/30 text-xs">
              Arzani Group Worldwide
            </p>
          </motion.div>
        </div>
      </section>

      {/* Browse more */}
      <section className="py-16 bg-background">
        <div className="container-luxury text-center">
          <Link
            to="/yachts"
            className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
          >
            Browse Full Fleet
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default YachtDetail;
